// yield-engine.js — Calculation engine for multi-strategy yield & liquidation analysis (eigenETH, royETH, vanilla wstETH/ETH), historical backtest, and multi-vault sweep
// No UI dependencies. Importable by React, Node scripts, or agent tools.

const PARAM_BOUNDS = {
  srYield:            { min: 0,    max: 15,   step: 0.1,  unit: "%",  label: "Dawn Senior srRoyUSDC Yield" },
  stakingYield:       { min: 0,    max: 8,    step: 0.1,  unit: "%",  label: "wstETH Staking Yield" },
  aaveSupplyYield:    { min: 0,    max: 0.5,  step: 0.01, unit: "%",  label: "wstETH Supply APY" },
  aaveLTV:            { min: 10,   max: 75,   step: 1,    unit: "%",  label: "Carry Trade Effective LTV",
                        dynamicMax: (cfg) => cfg.aaveLiqThreshold - 1 },
  usdcBorrowRate:     { min: 1,    max: 20,   step: 0.1,  unit: "%",  label: "USDC Borrow Rate" },
  aaveLiqThreshold:   { min: 80,   max: 99,   step: 0.5,  unit: "%",  label: "Carry Trade Liquidation Threshold" },
  morphoLTV:          { min: 0,    max: 90,   step: 1,    unit: "%",  label: "Loop Effective LTV",
                        dynamicMax: (cfg) => cfg.morphoLiqThreshold - 1 },
  ethBorrowPremium:   { min: 0,    max: 5,    step: 0.1,  unit: "%",  label: "ETH Borrow Rate Premium" },
  morphoLiqThreshold: { min: 77,   max: 95,   step: 1,    unit: "%",  label: "Loop Liquidation Threshold" },
  baseEthBorrowRate:  { min: 0,    max: 10,   step: 0.1,  unit: "%",  label: "Base ETH Borrow Rate (wstETH)" },
  vanillaSupplyYield: { min: 0,    max: 0.5,  step: 0.01, unit: "%",  label: "Vanilla wstETH Supply APY" },
  depegPct:           { min: 0,    max: 25,   step: 0.5,  unit: "%",  label: "royETH Depeg %" },
  drawdownWindow:     { min: 0,    max: 15,   step: 1,    unit: "d",  label: "Historical Drawdown Window (days)" },
  modelDepeg:         { type: "boolean", label: "Model royETH Depeg Risk" },
  mirrorAaveSupply:   { type: "boolean", label: "Mirror wstETH Supply APY" },
};

const DEFAULT_CONFIG = {
  srYield: 7,
  stakingYield: 2.7,
  aaveSupplyYield: 0.04,
  aaveLTV: 39,
  usdcBorrowRate: 5.6,
  aaveLiqThreshold: 83,
  morphoLTV: 66,
  ethBorrowPremium: 0.5,
  morphoLiqThreshold: 85,
  modelDepeg: true,
  depegPct: 10,
  baseEthBorrowRate: 2.2,
  vanillaSupplyYield: 0.02,
  mirrorAaveSupply: true,
  drawdownWindow: 0,
};

// Configurable constraint defaults
const DEFAULT_CONSTRAINT_CONFIG = {
  ethDropPct: 43,   // Aave: survive this % ETH/USD drop (≈ 1d historical trough worst)
  depegPct: 10,     // Morpho: survive this % royETH depeg (overridden by config.depegPct when modelDepeg=true)
};

// --- Historical drawdown data (ETH/USD, >20% drops since 2019) ---
// Source: eth_since_2019_drawdowns.csv — 283 events across 10 time windows

const VALID_WINDOWS = [1, 2, 3, 4, 5, 6, 7, 8, 14, 15];

const HISTORICAL_WORST_BY_WINDOW = {
   1:  { maxDrop: 42.93, date: "2020-03-12", count: 9 },
   2:  { maxDrop: 51.15, date: "2020-03-13", count: 22 },
   3:  { maxDrop: 52.59, date: "2020-03-13", count: 26 },
   4:  { maxDrop: 52.88, date: "2020-03-13", count: 30 },
   5:  { maxDrop: 53.24, date: "2020-03-12", count: 32 },
   6:  { maxDrop: 59.98, date: "2020-03-13", count: 34 },
   7:  { maxDrop: 60.91, date: "2020-03-13", count: 34 },
   8:  { maxDrop: 60.91, date: "2020-03-13", count: 33 },
  14:  { maxDrop: 60.91, date: "2020-03-13", count: 32 },
  15:  { maxDrop: 60.91, date: "2020-03-13", count: 31 },
};

// Trough drawdown data: worst rolling drawdown per window (1–15 days), not close-to-close
const HISTORICAL_ETH_EVENTS = [
  { window: 1, date: "2020-03-12", drawdown: 42.93, event: "COVID-19 Black Thursday crash" },
  { window: 1, date: "2021-05-19", drawdown: 42.24, event: "China mining ban / Tesla suspends BTC" },
  { window: 1, date: "2021-01-11", drawdown: 26.72, event: "Leveraged long liquidation cascade" },
  { window: 1, date: "2025-02-03", drawdown: 24.73, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 1, date: "2021-02-23", drawdown: 22.62, event: "US Treasury yield spike / risk-off" },
  { window: 1, date: "2021-09-07", drawdown: 22.05, event: "China crypto trading ban / El Salvador" },
  { window: 1, date: "2024-08-05", drawdown: 20.99, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 1, date: "2025-10-10", drawdown: 20.80, event: "Trump 100% China tariff flash crash" },
  { window: 1, date: "2021-01-21", drawdown: 20.53, event: "Double-dip leverage correction" },
  { window: 2, date: "2020-03-13", drawdown: 51.15, event: "COVID-19 Black Thursday crash" },
  { window: 2, date: "2021-05-19", drawdown: 42.24, event: "China mining ban / Tesla suspends BTC" },
  { window: 2, date: "2022-11-09", drawdown: 30.94, event: "FTX collapse" },
  { window: 2, date: "2025-02-03", drawdown: 30.76, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 2, date: "2021-02-23", drawdown: 28.76, event: "US Treasury yield spike / risk-off" },
  { window: 2, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 2, date: "2024-08-05", drawdown: 26.89, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 2, date: "2022-05-12", drawdown: 25.40, event: "LUNA/UST collapse" },
  { window: 2, date: "2021-01-22", drawdown: 24.28, event: "Double-dip leverage correction" },
  { window: 2, date: "2022-06-14", drawdown: 24.25, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 2, date: "2021-06-22", drawdown: 23.98, event: "China crackdown escalation / Fed hawkish" },
  { window: 2, date: "2025-10-10", drawdown: 23.58, event: "Trump 100% China tariff flash crash" },
  { window: 2, date: "2019-09-24", drawdown: 23.08, event: "Bakkt futures launch disappointment" },
  { window: 2, date: "2021-09-07", drawdown: 22.52, event: "China crypto trading ban / El Salvador" },
  { window: 2, date: "2022-01-22", drawdown: 22.35, event: "Fed rate hike signals / risk-off rotation" },
  { window: 2, date: "2019-07-15", drawdown: 21.92, event: "BTC correction from $13k rally" },
  { window: 2, date: "2021-12-04", drawdown: 21.85, event: "Omicron variant / Fed taper fears" },
  { window: 2, date: "2025-11-04", drawdown: 21.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 2, date: "2025-04-07", drawdown: 21.63, event: "Trump Liberation Day tariffs" },
  { window: 2, date: "2025-03-04", drawdown: 20.75, event: "Continued macro selloff / whale distribution" },
  { window: 2, date: "2026-01-31", drawdown: 20.21, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 2, date: "2020-09-03", drawdown: 20.11, event: "DeFi correction / SushiSwap selloff" },
  { window: 3, date: "2020-03-13", drawdown: 52.59, event: "COVID-19 Black Thursday crash" },
  { window: 3, date: "2021-05-19", drawdown: 45.58, event: "China mining ban / Tesla suspends BTC" },
  { window: 3, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 3, date: "2022-11-09", drawdown: 31.10, event: "FTX collapse" },
  { window: 3, date: "2022-06-15", drawdown: 29.03, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 3, date: "2024-08-05", drawdown: 28.92, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 3, date: "2021-02-23", drawdown: 28.76, event: "US Treasury yield spike / risk-off" },
  { window: 3, date: "2020-09-05", drawdown: 28.01, event: "DeFi correction / SushiSwap selloff" },
  { window: 3, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 3, date: "2019-07-16", drawdown: 26.75, event: "BTC correction from $13k rally" },
  { window: 3, date: "2022-05-12", drawdown: 25.40, event: "LUNA/UST collapse" },
  { window: 3, date: "2026-01-31", drawdown: 25.21, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 3, date: "2022-01-22", drawdown: 24.73, event: "Fed rate hike signals / risk-off rotation" },
  { window: 3, date: "2019-09-24", drawdown: 24.49, event: "Bakkt futures launch disappointment" },
  { window: 3, date: "2021-01-22", drawdown: 24.28, event: "Double-dip leverage correction" },
  { window: 3, date: "2021-06-22", drawdown: 23.98, event: "China crackdown escalation / Fed hawkish" },
  { window: 3, date: "2025-10-10", drawdown: 23.58, event: "Trump 100% China tariff flash crash" },
  { window: 3, date: "2021-12-04", drawdown: 23.14, event: "Omicron variant / Fed taper fears" },
  { window: 3, date: "2021-09-07", drawdown: 22.52, event: "China crypto trading ban / El Salvador" },
  { window: 3, date: "2025-04-07", drawdown: 22.03, event: "Trump Liberation Day tariffs" },
  { window: 3, date: "2021-09-21", drawdown: 22.02, event: "China crypto trading ban / El Salvador" },
  { window: 3, date: "2025-11-04", drawdown: 21.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 3, date: "2025-03-04", drawdown: 20.75, event: "Continued macro selloff / whale distribution" },
  { window: 3, date: "2024-12-20", drawdown: 20.29, event: "Hawkish Fed December / rate cut reset" },
  { window: 3, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 3, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 4, date: "2020-03-13", drawdown: 52.88, event: "COVID-19 Black Thursday crash" },
  { window: 4, date: "2021-05-19", drawdown: 46.33, event: "China mining ban / Tesla suspends BTC" },
  { window: 4, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 4, date: "2022-06-14", drawdown: 34.25, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 4, date: "2024-08-05", drawdown: 33.70, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 4, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 4, date: "2022-11-09", drawdown: 33.46, event: "FTX collapse" },
  { window: 4, date: "2022-05-12", drawdown: 30.55, event: "LUNA/UST collapse" },
  { window: 4, date: "2021-02-23", drawdown: 29.66, event: "US Treasury yield spike / risk-off" },
  { window: 4, date: "2019-07-16", drawdown: 28.56, event: "BTC correction from $13k rally" },
  { window: 4, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 4, date: "2022-01-24", drawdown: 27.62, event: "Fed rate hike signals / risk-off rotation" },
  { window: 4, date: "2025-10-10", drawdown: 26.19, event: "Trump 100% China tariff flash crash" },
  { window: 4, date: "2026-02-01", drawdown: 26.03, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 4, date: "2019-09-26", drawdown: 25.72, event: "Bakkt futures launch disappointment" },
  { window: 4, date: "2021-01-22", drawdown: 24.28, event: "Double-dip leverage correction" },
  { window: 4, date: "2021-06-22", drawdown: 23.98, event: "China crackdown escalation / Fed hawkish" },
  { window: 4, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 4, date: "2025-04-09", drawdown: 23.21, event: "Trump Liberation Day tariffs" },
  { window: 4, date: "2019-01-13", drawdown: 23.02, event: "Crypto winter continuation" },
  { window: 4, date: "2021-09-07", drawdown: 22.52, event: "China crypto trading ban / El Salvador" },
  { window: 4, date: "2020-02-27", drawdown: 22.32, event: "COVID-19 initial fears" },
  { window: 4, date: "2024-12-20", drawdown: 22.30, event: "Hawkish Fed December / rate cut reset" },
  { window: 4, date: "2021-09-21", drawdown: 22.02, event: "China crypto trading ban / El Salvador" },
  { window: 4, date: "2025-11-04", drawdown: 21.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 4, date: "2019-11-22", drawdown: 21.17, event: "China crypto crackdown / PlusToken fears" },
  { window: 4, date: "2025-02-27", drawdown: 20.94, event: "Continued macro selloff / whale distribution" },
  { window: 4, date: "2022-01-08", drawdown: 20.38, event: "Fed rate hike signals / risk-off rotation" },
  { window: 4, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 4, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 5, date: "2020-03-12", drawdown: 53.24, event: "COVID-19 Black Thursday crash" },
  { window: 5, date: "2021-05-19", drawdown: 52.13, event: "China mining ban / Tesla suspends BTC" },
  { window: 5, date: "2022-06-14", drawdown: 38.84, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 5, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 5, date: "2024-08-05", drawdown: 34.31, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 5, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 5, date: "2022-05-12", drawdown: 33.68, event: "LUNA/UST collapse" },
  { window: 5, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 5, date: "2022-01-24", drawdown: 29.83, event: "Fed rate hike signals / risk-off rotation" },
  { window: 5, date: "2019-07-17", drawdown: 29.78, event: "BTC correction from $13k rally" },
  { window: 5, date: "2021-02-23", drawdown: 29.66, event: "US Treasury yield spike / risk-off" },
  { window: 5, date: "2026-02-02", drawdown: 28.19, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 5, date: "2021-06-22", drawdown: 28.01, event: "China crackdown escalation / Fed hawkish" },
  { window: 5, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 5, date: "2019-09-26", drawdown: 27.09, event: "Bakkt futures launch disappointment" },
  { window: 5, date: "2025-02-28", drawdown: 26.41, event: "Continued macro selloff / whale distribution" },
  { window: 5, date: "2025-10-10", drawdown: 26.19, event: "Trump 100% China tariff flash crash" },
  { window: 5, date: "2021-09-21", drawdown: 25.06, event: "China crypto trading ban / El Salvador" },
  { window: 5, date: "2021-01-22", drawdown: 24.28, event: "Double-dip leverage correction" },
  { window: 5, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 5, date: "2019-11-25", drawdown: 23.85, event: "China crypto crackdown / PlusToken fears" },
  { window: 5, date: "2025-04-09", drawdown: 23.61, event: "Trump Liberation Day tariffs" },
  { window: 5, date: "2019-01-13", drawdown: 23.02, event: "Crypto winter continuation" },
  { window: 5, date: "2024-04-13", drawdown: 22.54, event: "Iran-Israel tensions / Middle East risk" },
  { window: 5, date: "2021-09-07", drawdown: 22.52, event: "China crypto trading ban / El Salvador" },
  { window: 5, date: "2020-02-27", drawdown: 22.32, event: "COVID-19 initial fears" },
  { window: 5, date: "2024-12-20", drawdown: 22.30, event: "Hawkish Fed December / rate cut reset" },
  { window: 5, date: "2025-11-04", drawdown: 21.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 5, date: "2022-09-19", drawdown: 21.25, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 5, date: "2022-01-08", drawdown: 20.38, event: "Fed rate hike signals / risk-off rotation" },
  { window: 5, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 5, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 6, date: "2020-03-13", drawdown: 59.98, event: "COVID-19 Black Thursday crash" },
  { window: 6, date: "2021-05-19", drawdown: 52.13, event: "China mining ban / Tesla suspends BTC" },
  { window: 6, date: "2022-06-15", drawdown: 42.69, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 6, date: "2024-08-05", drawdown: 35.26, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 6, date: "2022-05-12", drawdown: 35.13, event: "LUNA/UST collapse" },
  { window: 6, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 6, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 6, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 6, date: "2026-02-05", drawdown: 32.70, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 6, date: "2019-07-16", drawdown: 31.94, event: "BTC correction from $13k rally" },
  { window: 6, date: "2022-01-24", drawdown: 31.34, event: "Fed rate hike signals / risk-off rotation" },
  { window: 6, date: "2021-02-23", drawdown: 29.66, event: "US Treasury yield spike / risk-off" },
  { window: 6, date: "2021-06-21", drawdown: 28.49, event: "China crackdown escalation / Fed hawkish" },
  { window: 6, date: "2019-09-26", drawdown: 27.94, event: "Bakkt futures launch disappointment" },
  { window: 6, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 6, date: "2025-02-28", drawdown: 26.41, event: "Continued macro selloff / whale distribution" },
  { window: 6, date: "2025-10-10", drawdown: 26.19, event: "Trump 100% China tariff flash crash" },
  { window: 6, date: "2021-09-21", drawdown: 25.97, event: "China crypto trading ban / El Salvador" },
  { window: 6, date: "2025-04-07", drawdown: 25.72, event: "Trump Liberation Day tariffs" },
  { window: 6, date: "2019-11-25", drawdown: 24.60, event: "China crypto crackdown / PlusToken fears" },
  { window: 6, date: "2021-01-22", drawdown: 24.28, event: "Double-dip leverage correction" },
  { window: 6, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 6, date: "2019-01-13", drawdown: 23.48, event: "Crypto winter continuation" },
  { window: 6, date: "2024-04-13", drawdown: 22.54, event: "Iran-Israel tensions / Middle East risk" },
  { window: 6, date: "2021-09-07", drawdown: 22.52, event: "China crypto trading ban / El Salvador" },
  { window: 6, date: "2020-02-27", drawdown: 22.32, event: "COVID-19 initial fears" },
  { window: 6, date: "2022-01-10", drawdown: 22.31, event: "Fed rate hike signals / risk-off rotation" },
  { window: 6, date: "2022-09-18", drawdown: 22.31, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 6, date: "2024-12-20", drawdown: 22.30, event: "Hawkish Fed December / rate cut reset" },
  { window: 6, date: "2025-11-04", drawdown: 21.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 6, date: "2024-03-19", drawdown: 21.39, event: "Post-BTC ETF rally correction" },
  { window: 6, date: "2022-08-20", drawdown: 20.78, event: "Post-Merge anticipation selloff" },
  { window: 6, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 6, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 7, date: "2020-03-13", drawdown: 60.91, event: "COVID-19 Black Thursday crash" },
  { window: 7, date: "2021-05-19", drawdown: 52.13, event: "China mining ban / Tesla suspends BTC" },
  { window: 7, date: "2022-06-15", drawdown: 42.81, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 7, date: "2022-05-12", drawdown: 36.41, event: "LUNA/UST collapse" },
  { window: 7, date: "2019-07-16", drawdown: 36.10, event: "BTC correction from $13k rally" },
  { window: 7, date: "2024-08-05", drawdown: 36.08, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 7, date: "2026-02-05", drawdown: 35.47, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 7, date: "2021-06-22", drawdown: 34.60, event: "China crackdown escalation / Fed hawkish" },
  { window: 7, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 7, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 7, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 7, date: "2021-02-28", drawdown: 32.81, event: "US Treasury yield spike / risk-off" },
  { window: 7, date: "2022-01-24", drawdown: 32.38, event: "Fed rate hike signals / risk-off rotation" },
  { window: 7, date: "2019-09-26", drawdown: 28.99, event: "Bakkt futures launch disappointment" },
  { window: 7, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 7, date: "2019-01-13", drawdown: 26.41, event: "Crypto winter continuation" },
  { window: 7, date: "2025-02-28", drawdown: 26.41, event: "Continued macro selloff / whale distribution" },
  { window: 7, date: "2025-10-10", drawdown: 26.19, event: "Trump 100% China tariff flash crash" },
  { window: 7, date: "2021-09-21", drawdown: 25.97, event: "China crypto trading ban / El Salvador" },
  { window: 7, date: "2019-11-25", drawdown: 25.90, event: "China crypto crackdown / PlusToken fears" },
  { window: 7, date: "2025-04-07", drawdown: 25.72, event: "Trump Liberation Day tariffs" },
  { window: 7, date: "2022-09-19", drawdown: 24.88, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 7, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 7, date: "2024-03-20", drawdown: 23.63, event: "Post-BTC ETF rally correction" },
  { window: 7, date: "2025-11-04", drawdown: 23.08, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 7, date: "2022-08-20", drawdown: 22.56, event: "Post-Merge anticipation selloff" },
  { window: 7, date: "2024-04-13", drawdown: 22.54, event: "Iran-Israel tensions / Middle East risk" },
  { window: 7, date: "2020-02-27", drawdown: 22.32, event: "COVID-19 initial fears" },
  { window: 7, date: "2022-01-10", drawdown: 22.31, event: "Fed rate hike signals / risk-off rotation" },
  { window: 7, date: "2024-12-20", drawdown: 22.30, event: "Hawkish Fed December / rate cut reset" },
  { window: 7, date: "2022-02-22", drawdown: 21.04, event: "Russia invades Ukraine" },
  { window: 7, date: "2025-01-13", drawdown: 20.82, event: "BOJ rate hike fears / year-start risk-off" },
  { window: 7, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 7, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 8, date: "2020-03-13", drawdown: 60.91, event: "COVID-19 Black Thursday crash" },
  { window: 8, date: "2021-05-19", drawdown: 53.16, event: "China mining ban / Tesla suspends BTC" },
  { window: 8, date: "2022-06-18", drawdown: 46.18, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 8, date: "2022-05-12", drawdown: 40.55, event: "LUNA/UST collapse" },
  { window: 8, date: "2026-02-05", drawdown: 39.51, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 8, date: "2019-07-17", drawdown: 37.20, event: "BTC correction from $13k rally" },
  { window: 8, date: "2024-08-05", drawdown: 36.08, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 8, date: "2022-01-24", drawdown: 35.17, event: "Fed rate hike signals / risk-off rotation" },
  { window: 8, date: "2021-06-22", drawdown: 34.60, event: "China crackdown escalation / Fed hawkish" },
  { window: 8, date: "2025-02-03", drawdown: 34.53, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 8, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 8, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 8, date: "2021-02-28", drawdown: 32.81, event: "US Treasury yield spike / risk-off" },
  { window: 8, date: "2019-09-26", drawdown: 28.99, event: "Bakkt futures launch disappointment" },
  { window: 8, date: "2025-03-10", drawdown: 28.06, event: "Continued macro selloff / whale distribution" },
  { window: 8, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 8, date: "2019-11-25", drawdown: 27.72, event: "China crypto crackdown / PlusToken fears" },
  { window: 8, date: "2025-04-09", drawdown: 27.22, event: "Trump Liberation Day tariffs" },
  { window: 8, date: "2022-09-19", drawdown: 26.93, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 8, date: "2019-01-13", drawdown: 26.41, event: "Crypto winter continuation" },
  { window: 8, date: "2025-10-10", drawdown: 26.19, event: "Trump 100% China tariff flash crash" },
  { window: 8, date: "2022-02-24", drawdown: 26.18, event: "Russia invades Ukraine" },
  { window: 8, date: "2021-09-21", drawdown: 25.97, event: "China crypto trading ban / El Salvador" },
  { window: 8, date: "2025-11-04", drawdown: 25.66, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 8, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 8, date: "2024-03-20", drawdown: 23.63, event: "Post-BTC ETF rally correction" },
  { window: 8, date: "2022-08-20", drawdown: 22.56, event: "Post-Merge anticipation selloff" },
  { window: 8, date: "2024-04-13", drawdown: 22.54, event: "Iran-Israel tensions / Middle East risk" },
  { window: 8, date: "2024-12-20", drawdown: 22.30, event: "Hawkish Fed December / rate cut reset" },
  { window: 8, date: "2019-08-15", drawdown: 21.31, event: "US-China trade war escalation" },
  { window: 8, date: "2025-01-13", drawdown: 20.82, event: "BOJ rate hike fears / year-start risk-off" },
  { window: 8, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 8, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 14, date: "2020-03-13", drawdown: 60.91, event: "COVID-19 Black Thursday crash" },
  { window: 14, date: "2021-05-23", drawdown: 58.32, event: "China mining ban / Tesla suspends BTC" },
  { window: 14, date: "2022-06-18", drawdown: 51.80, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 14, date: "2026-02-06", drawdown: 42.14, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 14, date: "2022-05-12", drawdown: 40.55, event: "LUNA/UST collapse" },
  { window: 14, date: "2024-08-05", drawdown: 39.04, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 14, date: "2019-07-17", drawdown: 38.07, event: "BTC correction from $13k rally" },
  { window: 14, date: "2022-01-24", drawdown: 35.58, event: "Fed rate hike signals / risk-off rotation" },
  { window: 14, date: "2025-02-03", drawdown: 35.25, event: "BOJ rate hike / leveraged liquidation cascade" },
  { window: 14, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 14, date: "2021-02-28", drawdown: 33.65, event: "US Treasury yield spike / risk-off" },
  { window: 14, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 14, date: "2022-09-21", drawdown: 30.78, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 14, date: "2019-09-26", drawdown: 28.99, event: "Bakkt futures launch disappointment" },
  { window: 14, date: "2019-11-25", drawdown: 28.93, event: "China crypto crackdown / PlusToken fears" },
  { window: 14, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 14, date: "2022-02-24", drawdown: 27.39, event: "Russia invades Ukraine" },
  { window: 14, date: "2025-11-21", drawdown: 26.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 14, date: "2022-08-27", drawdown: 26.60, event: "Post-Merge anticipation selloff" },
  { window: 14, date: "2019-01-13", drawdown: 26.41, event: "Crypto winter continuation" },
  { window: 14, date: "2021-09-21", drawdown: 25.97, event: "China crypto trading ban / El Salvador" },
  { window: 14, date: "2025-06-22", drawdown: 24.77, event: "Mid-rally profit-taking correction" },
  { window: 14, date: "2024-03-20", drawdown: 24.76, event: "Post-BTC ETF rally correction" },
  { window: 14, date: "2019-08-15", drawdown: 23.94, event: "US-China trade war escalation" },
  { window: 14, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 14, date: "2024-12-20", drawdown: 22.66, event: "Hawkish Fed December / rate cut reset" },
  { window: 14, date: "2024-04-13", drawdown: 22.54, event: "Iran-Israel tensions / Middle East risk" },
  { window: 14, date: "2024-09-06", drawdown: 22.33, event: "Pre-FOMC volatility / September seasonality" },
  { window: 14, date: "2019-03-04", drawdown: 21.03, event: "Q1 2019 market correction" },
  { window: 14, date: "2019-12-18", drawdown: 20.81, event: "Year-end low liquidity selloff" },
  { window: 14, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 14, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
  { window: 15, date: "2020-03-13", drawdown: 60.91, event: "COVID-19 Black Thursday crash" },
  { window: 15, date: "2021-05-23", drawdown: 58.32, event: "China mining ban / Tesla suspends BTC" },
  { window: 15, date: "2022-06-18", drawdown: 51.80, event: "Three Arrows Capital / Celsius insolvency" },
  { window: 15, date: "2026-02-06", drawdown: 42.14, event: "BOJ hike + Fed Warsh hawkish + weak labor data" },
  { window: 15, date: "2022-05-12", drawdown: 40.55, event: "LUNA/UST collapse" },
  { window: 15, date: "2024-08-05", drawdown: 39.98, event: "Japan carry trade unwind / BOJ rate hike" },
  { window: 15, date: "2019-07-17", drawdown: 38.07, event: "BTC correction from $13k rally" },
  { window: 15, date: "2025-03-10", drawdown: 35.75, event: "Continued macro selloff / whale distribution" },
  { window: 15, date: "2022-01-24", drawdown: 35.58, event: "Fed rate hike signals / risk-off rotation" },
  { window: 15, date: "2022-11-09", drawdown: 34.15, event: "FTX collapse" },
  { window: 15, date: "2021-02-28", drawdown: 33.65, event: "US Treasury yield spike / risk-off" },
  { window: 15, date: "2020-09-05", drawdown: 33.60, event: "DeFi correction / SushiSwap selloff" },
  { window: 15, date: "2021-09-21", drawdown: 31.87, event: "China crypto trading ban / El Salvador" },
  { window: 15, date: "2022-09-21", drawdown: 30.78, event: "ETH Merge sell-the-news / CPI shock" },
  { window: 15, date: "2019-11-25", drawdown: 29.38, event: "China crypto crackdown / PlusToken fears" },
  { window: 15, date: "2019-09-26", drawdown: 28.99, event: "Bakkt futures launch disappointment" },
  { window: 15, date: "2022-02-24", drawdown: 28.73, event: "Russia invades Ukraine" },
  { window: 15, date: "2021-01-11", drawdown: 27.80, event: "Leveraged long liquidation cascade" },
  { window: 15, date: "2022-08-28", drawdown: 27.80, event: "Post-Merge anticipation selloff" },
  { window: 15, date: "2025-11-21", drawdown: 26.68, event: "Oct crash continuation / Fed hawkish / ETF outflows" },
  { window: 15, date: "2019-01-13", drawdown: 26.41, event: "Crypto winter continuation" },
  { window: 15, date: "2025-06-22", drawdown: 24.77, event: "Mid-rally profit-taking correction" },
  { window: 15, date: "2024-03-20", drawdown: 24.76, event: "Post-BTC ETF rally correction" },
  { window: 15, date: "2019-08-15", drawdown: 23.94, event: "US-China trade war escalation" },
  { window: 15, date: "2021-12-04", drawdown: 23.88, event: "Omicron variant / Fed taper fears" },
  { window: 15, date: "2024-12-20", drawdown: 22.66, event: "Hawkish Fed December / rate cut reset" },
  { window: 15, date: "2024-09-06", drawdown: 22.33, event: "Pre-FOMC volatility / September seasonality" },
  { window: 15, date: "2019-03-04", drawdown: 21.03, event: "Q1 2019 market correction" },
  { window: 15, date: "2019-12-18", drawdown: 20.81, event: "Year-end low liquidity selloff" },
  { window: 15, date: "2020-11-26", drawdown: 20.21, event: "Post-rally Thanksgiving pullback" },
  { window: 15, date: "2021-04-18", drawdown: 20.14, event: "Turkey crypto ban / leveraged liquidations" },
];
/**
 * Resolve a requested drawdown window to the nearest valid window <= requested.
 * E.g., resolveWindow(10) returns 8 (nearest available data).
 */
function resolveWindow(requested) {
  let best = VALID_WINDOWS[0];
  for (const w of VALID_WINDOWS) {
    if (w <= requested) best = w;
  }
  return best;
}

/**
 * Get the effective max for a parameter, accounting for dynamic coupling
 */
function getEffectiveMax(param, config) {
  const bounds = PARAM_BOUNDS[param];
  if (!bounds || bounds.type === "boolean") return null;
  const staticMax = bounds.max;
  if (bounds.dynamicMax) {
    return Math.min(staticMax, bounds.dynamicMax(config));
  }
  return staticMax;
}

/**
 * Get the effective min for a parameter
 */
function getEffectiveMin(param) {
  const bounds = PARAM_BOUNDS[param];
  if (!bounds || bounds.type === "boolean") return null;
  return bounds.min;
}

/**
 * Clamp a config to valid bounds. Returns a new config object.
 */
function clampConfig(config) {
  const clamped = { ...config };
  for (const [param, bounds] of Object.entries(PARAM_BOUNDS)) {
    if (bounds.type === "boolean") continue;
    if (bounds.fixed) {
      clamped[param] = bounds.min;
      continue;
    }
    if (clamped[param] == null) continue;
    const min = bounds.min;
    const max = getEffectiveMax(param, clamped);
    clamped[param] = Math.max(min, Math.min(max, clamped[param]));
  }
  return clamped;
}

/**
 * Core analysis function. Takes a config, returns full computed state.
 */
function analyze(rawConfig, constraintConfig) {
  const config = clampConfig({ ...DEFAULT_CONFIG, ...rawConfig });
  const cc = { ...DEFAULT_CONSTRAINT_CONFIG, ...constraintConfig };

  // If drawdownWindow is set, override ethDropPct with historical worst
  let resolvedWindow = null;
  let windowHist = null;
  if (config.drawdownWindow > 0) {
    resolvedWindow = resolveWindow(config.drawdownWindow);
    windowHist = HISTORICAL_WORST_BY_WINDOW[resolvedWindow];
    if (windowHist) {
      cc.ethDropPct = windowHist.maxDrop;
    }
  }

  // Unpack to decimals
  const sr   = config.srYield / 100;
  const sk   = config.stakingYield / 100;
  const asup = config.aaveSupplyYield / 100;
  const altv = config.aaveLTV / 100;
  const ubr  = config.usdcBorrowRate / 100;
  const alt  = config.aaveLiqThreshold / 100;
  const mltv = config.morphoLTV / 100;
  const bebr = config.baseEthBorrowRate / 100;
  const ebp  = config.ethBorrowPremium / 100;
  const ebr  = bebr + ebp;
  const mlt  = config.morphoLiqThreshold / 100;
  const dp   = config.depegPct / 100;
  const vsup = config.mirrorAaveSupply ? asup : config.vanillaSupplyYield / 100;

  // --- Yields ---
  const dawnYield = sr;

  // royETH yield: staking + supply + LTV * (srYield - usdcBorrow)
  const usdcSpread = sr - ubr;
  const royETHYield = sk + asup + altv * usdcSpread;
  const royETHStakingComponent = sk;
  const royETHSupplyComponent = asup;
  const royETHSpreadComponent = altv * usdcSpread;

  // eigenETH yield (leveraged)
  const leverage = mltv > 0 ? 1 / (1 - mltv) : 1;
  const eigenETHYield = leverage * royETHYield - (leverage - 1) * ebr;
  const eigenGross = leverage * royETHYield;
  const eigenBorrowCost = (leverage - 1) * ebr;

  // Vanilla wstETH/ETH loop
  const vanillaYield = leverage * (sk + vsup) - (leverage - 1) * bebr;
  const vanillaGross = leverage * (sk + vsup);
  const vanillaBorrowCost = (leverage - 1) * bebr;

  // 1x wstETH
  const oneXwstETH = sk;

  // --- Spreads ---
  const morphoSpread = royETHYield - ebr;
  const vanillaSpread = sk + vsup - bebr;

  // --- Comparisons ---
  const vsVanilla = eigenETHYield - vanillaYield;
  const vs1x = eigenETHYield - oneXwstETH;

  // --- Liquidation ---
  // Aave: wstETH collateral vs USDC debt, ETH/USD drawdown
  const aaveMaxDrop = 1 - altv / alt;
  const aaveHF = alt / altv;

  // Morpho: royETH collateral vs ETH debt, depeg risk
  const morphoMaxDepeg = 1 - mltv / mlt;
  const morphoHF = mltv > 0 ? mlt / mltv : Infinity;
  const morphoLTVAfterDepeg = config.modelDepeg ? mltv / (1 - dp) : mltv;
  const morphoHFAfterDepeg = morphoLTVAfterDepeg > 0 ? mlt / morphoLTVAfterDepeg : Infinity;

  // --- Safe LTV Ranges ---
  const configuredDropPct = cc.ethDropPct / 100;
  const configuredDepegPct = (config.modelDepeg ? config.depegPct : cc.depegPct) / 100;

  const aaveMaxSafeLTV = alt * (1 - configuredDropPct);
  const morphoMaxSafeLTV = mlt * (1 - configuredDepegPct);

  // --- Constraints ---
  const constraints = {
    royETHPositive: {
      pass: royETHYield > 0,
      value: royETHYield,
      headroom: royETHYield,
      description: `royETH yield is ${(royETHYield * 100).toFixed(2)}%`,
    },
    morphoSpreadPositive: {
      pass: morphoSpread > 0,
      value: morphoSpread,
      headroom: morphoSpread,
      description: `Morpho loop spread: ${(morphoSpread * 100).toFixed(2)}%`,
    },
    vanillaSpreadPositive: {
      pass: vanillaSpread > 0,
      value: vanillaSpread,
      headroom: vanillaSpread,
      description: `Vanilla loop spread: ${(vanillaSpread * 100).toFixed(2)}%`,
    },
    eigenBeatVanilla: {
      pass: vsVanilla >= 0,
      value: vsVanilla,
      headroom: vsVanilla,
      description: `eigenETH vs vanilla: ${(vsVanilla * 100).toFixed(2)}%`,
    },
    eigenBeat1x: {
      pass: vs1x >= 0,
      value: vs1x,
      headroom: vs1x,
      description: `eigenETH vs 1x wstETH: ${(vs1x * 100).toFixed(2)}%`,
    },
    aaveSurviveDrop: {
      pass: altv <= aaveMaxSafeLTV,
      dropPct: configuredDropPct,
      maxLTV: aaveMaxSafeLTV,
      currentLTV: altv,
      headroom: aaveMaxSafeLTV - altv,
      historicalWindow: resolvedWindow,
      description: resolvedWindow
        ? `Aave survives ${resolvedWindow}d worst (${cc.ethDropPct.toFixed(1)}% drop, ${windowHist.date}): max LTV ${(aaveMaxSafeLTV * 100).toFixed(1)}%, current ${(altv * 100).toFixed(1)}%`
        : `Aave survives ${cc.ethDropPct}% ETH drop: max LTV ${(aaveMaxSafeLTV * 100).toFixed(1)}%, current ${(altv * 100).toFixed(1)}%`,
    },
    morphoSurviveDepeg: {
      pass: mltv <= morphoMaxSafeLTV,
      depegPct: configuredDepegPct,
      maxLTV: morphoMaxSafeLTV,
      currentLTV: mltv,
      headroom: morphoMaxSafeLTV - mltv,
      description: `Morpho survives ${(configuredDepegPct * 100).toFixed(0)}% depeg: max LTV ${(morphoMaxSafeLTV * 100).toFixed(1)}%, current ${(mltv * 100).toFixed(1)}%`,
    },
  };

  const passingCount = Object.values(constraints).filter(c => c.pass).length;
  const totalCount = Object.keys(constraints).length;
  const allPass = passingCount === totalCount;
  const firstFailure = Object.entries(constraints).find(([, c]) => !c.pass)?.[0] || null;

  // --- Critical Thresholds ---
  // Solve backwards: for each constraint, what's the critical value of the most relevant param?
  const thresholds = {
    maxAaveLTV: {
      value: aaveMaxSafeLTV * 100,
      constraint: "aaveSurviveDrop",
      description: resolvedWindow
        ? `Max Aave LTV to survive ${resolvedWindow}d worst (${cc.ethDropPct.toFixed(1)}% drop)`
        : `Max Aave LTV to survive ${cc.ethDropPct}% ETH drop`,
    },
    maxMorphoLTV: {
      value: morphoMaxSafeLTV * 100,
      constraint: "morphoSurviveDepeg",
      description: `Max Morpho LTV to survive ${(configuredDepegPct * 100).toFixed(0)}% royETH depeg`,
    },
    maxBorrowPremium: {
      // eigenETH > vanilla when: leverage * royETH - (leverage-1) * (base+premium) > leverage * (sk+vsup) - (leverage-1) * base
      // => leverage * (royETH - sk - vsup) > (leverage-1) * premium
      // => premium < leverage * (royETH - sk - vsup) / (leverage-1)
      value: leverage > 1 ? ((leverage * (royETHYield - sk - vsup) / (leverage - 1)) * 100) : Infinity,
      constraint: "eigenBeatVanilla",
      description: "Max borrow premium where eigenETH beats vanilla",
    },
    breakEvenSrYield: {
      // royETH > 0 when: sk + asup + altv * (sr - ubr) > 0
      // => sr > ubr - (sk + asup) / altv
      value: altv > 0 ? (ubr - (sk + asup) / altv) * 100 : 0,
      constraint: "royETHPositive",
      description: "Min srRoyUSDC yield for royETH to be positive",
    },
    breakEvenBaseRate: {
      // morphoSpread > 0 when: royETH > ebr = base + premium
      // => base < royETH - premium
      value: (royETHYield - ebp) * 100,
      constraint: "morphoSpreadPositive",
      description: "Max base ETH borrow rate for positive morpho spread",
    },
  };

  // --- Aave Drawdown Table ---
  const aaveDrawdowns = [];
  for (let drop = 0; drop <= 80; drop += 5) {
    const d = drop / 100;
    const newLTV = altv / (1 - d);
    const hf = alt / newLTV;
    aaveDrawdowns.push({
      drop,
      ltv: newLTV,
      hf,
      liquidated: newLTV >= alt,
    });
  }

  // --- Morpho Depeg Table ---
  const morphoDepegScenarios = [];
  for (let peg = 0; peg <= 25; peg += 1) {
    const p = peg / 100;
    const newLTV = mltv > 0 ? mltv / (1 - p) : 0;
    const hf = newLTV > 0 ? mlt / newLTV : Infinity;
    morphoDepegScenarios.push({
      depeg: peg,
      ltv: newLTV,
      hf,
      liquidated: newLTV >= mlt,
    });
  }

  return {
    config,
    yields: {
      dawn: dawnYield,
      royETH: royETHYield,
      royETHComponents: {
        staking: royETHStakingComponent,
        supply: royETHSupplyComponent,
        spread: royETHSpreadComponent,
      },
      eigenETH: eigenETHYield,
      eigenETHComponents: {
        gross: eigenGross,
        borrowCost: eigenBorrowCost,
      },
      vanilla: vanillaYield,
      vanillaComponents: {
        gross: vanillaGross,
        borrowCost: vanillaBorrowCost,
      },
      oneXwstETH,
    },
    spreads: {
      morphoLoop: morphoSpread,
      vanillaLoop: vanillaSpread,
    },
    comparisons: {
      vsVanilla,
      vs1x,
    },
    leverage,
    effectiveBorrowRate: ebr,
    liquidation: {
      aave: {
        maxDrop: aaveMaxDrop,
        healthFactor: aaveHF,
        drawdowns: aaveDrawdowns,
      },
      morpho: {
        maxDepeg: morphoMaxDepeg,
        healthFactor: morphoHF,
        hfAfterDepeg: morphoHFAfterDepeg,
        ltvAfterDepeg: morphoLTVAfterDepeg,
        depegScenarios: morphoDepegScenarios,
      },
    },
    constraints,
    constraintsSummary: {
      passingCount,
      totalCount,
      allPass,
      firstFailure,
    },
    safeLTV: {
      aave: {
        max: aaveMaxSafeLTV * 100,
        current: altv * 100,
        headroom: (aaveMaxSafeLTV - altv) * 100,
        binding: "aaveSurviveDrop",
      },
      morpho: {
        max: morphoMaxSafeLTV * 100,
        current: mltv * 100,
        headroom: (morphoMaxSafeLTV - mltv) * 100,
        binding: "morphoSurviveDepeg",
      },
    },
    thresholds,
  };
}

/**
 * Sweep a single parameter across a range, returning analyze results at each step.
 */
function sweep(rawConfig, param, range, step) {
  const config = { ...DEFAULT_CONFIG, ...rawConfig };
  const bounds = PARAM_BOUNDS[param];

  if (!bounds) throw new Error(`Unknown parameter: ${param}`);
  if (bounds.fixed) throw new Error(`Cannot sweep fixed parameter: ${param}`);

  if (bounds.type === "boolean") {
    return [true, false].map(value => {
      const modified = { ...config, [param]: value };
      const result = analyze(modified);
      return {
        paramValue: value,
        ...result,
      };
    });
  }

  const sweepMin = range?.min ?? bounds.min;
  const sweepMax = range?.max ?? bounds.max;
  const sweepStep = step ?? bounds.step;
  const results = [];

  // Use epsilon to handle floating point in loop termination
  for (let v = sweepMin; v <= sweepMax + sweepStep * 0.001; v += sweepStep) {
    const decimals = Math.max(0, -Math.floor(Math.log10(sweepStep)));
    const value = parseFloat((Math.round(v / sweepStep) * sweepStep).toFixed(decimals));
    if (value > sweepMax) break;

    const modified = { ...config, [param]: value };
    // Recalculate dynamic max for coupled params
    const effectiveMax = getEffectiveMax(param, modified);
    if (effectiveMax !== null && value > effectiveMax) continue;

    const result = analyze(modified);
    results.push({
      paramValue: value,
      firstFailure: result.constraintsSummary.firstFailure,
      allPass: result.constraintsSummary.allPass,
      ...result,
    });
  }

  return results;
}

/**
 * Run multiple sweeps in one call.
 */
function multiSweep(rawConfig, sweeps) {
  const results = {};
  for (const s of sweeps) {
    results[s.param] = sweep(rawConfig, s.param, s.range, s.step);
  }
  return results;
}

/**
 * Find constraint boundary flip points for all sweepable parameters.
 */
function findBoundaries(rawConfig) {
  const config = { ...DEFAULT_CONFIG, ...rawConfig };
  const boundaries = {};

  for (const [param, bounds] of Object.entries(PARAM_BOUNDS)) {
    if (bounds.fixed) continue;

    if (bounds.type === "boolean") {
      const results = sweep(config, param);
      boundaries[param] = {
        true: results.find(r => r.paramValue === true)?.constraintsSummary,
        false: results.find(r => r.paramValue === false)?.constraintsSummary,
      };
      continue;
    }

    const results = sweep(config, param);
    if (results.length === 0) continue;

    const constraintNames = Object.keys(results[0].constraints);
    const paramBoundaries = {};

    for (const constraint of constraintNames) {
      // Find the first flip point
      let flipAt = null;
      let direction = null;

      for (let i = 1; i < results.length; i++) {
        const prev = results[i - 1].constraints[constraint].pass;
        const curr = results[i].constraints[constraint].pass;
        if (prev !== curr) {
          flipAt = results[i].paramValue;
          direction = prev ? "exceeding" : "below";
          break;
        }
      }

      if (flipAt !== null) {
        paramBoundaries[constraint] = { flipsAt: flipAt, direction };
      } else {
        const allPass = results.every(r => r.constraints[constraint].pass);
        paramBoundaries[constraint] = {
          flipsAt: null,
          note: allPass ? "passes across full range" : "fails across full range",
        };
      }
    }

    boundaries[param] = paramBoundaries;
  }

  return boundaries;
}

/**
 * Backtest current config against all historical ETH drawdown events.
 * Aave is tested per-event against the actual historical drop.
 * Morpho uses the config's depegPct (no historical royETH depeg data).
 * Both must survive simultaneously (correlated stress).
 */
function backtest(rawConfig) {
  const config = clampConfig({ ...DEFAULT_CONFIG, ...rawConfig });
  const altv = config.aaveLTV / 100;
  const alt = config.aaveLiqThreshold / 100;
  const mltv = config.morphoLTV / 100;
  const mlt = config.morphoLiqThreshold / 100;
  const dp = (config.modelDepeg ? config.depegPct : DEFAULT_CONSTRAINT_CONFIG.depegPct) / 100;

  // Morpho uses config depegPct (constant across all events)
  const morphoMaxSafeLTV = mlt * (1 - dp);
  const morphoSurvives = mltv <= morphoMaxSafeLTV;

  // Test each historical ETH event against Aave position
  const eventResults = HISTORICAL_ETH_EVENTS.map(event => {
    const drop = event.drawdown / 100;
    const aaveMaxSafeLTV = alt * (1 - drop);
    const aaveSurvives = altv <= aaveMaxSafeLTV;
    // What % of USDC debt must be repaid to survive this drawdown?
    // After repaying fraction r: effective_LTV = altv * (1-r) / (1-drop)
    // Need: altv * (1-r) / (1-drop) <= alt  →  r >= 1 - (1-drop)*alt/altv
    const repayPct = altv > 0 ? Math.max(0, 1 - (1 - drop) * alt / altv) : 0;
    return {
      window: event.window,
      date: event.date,
      drawdown: event.drawdown,
      event: event.event,
      aaveSurvives,
      survives: aaveSurvives && morphoSurvives,
      aaveHeadroom: (aaveMaxSafeLTV - altv) * 100,
      repayPct: repayPct * 100, // as percentage of total USDC debt
    };
  });

  // Summarize by window
  const windowResults = VALID_WINDOWS.map(w => {
    const events = eventResults.filter(e => e.window === w);
    const allPass = events.every(e => e.survives);
    const failures = events.filter(e => !e.survives);
    const worstEvent = events.reduce((a, b) => b.drawdown > a.drawdown ? b : a);
    return {
      window: w,
      totalEvents: events.length,
      allPass,
      passCount: events.filter(e => e.survives).length,
      failCount: failures.length,
      worstDrop: worstEvent.drawdown,
      worstDate: worstEvent.date,
      worstEventName: worstEvent.event,
      failures,
    };
  });

  return {
    morphoSurvives,
    depegPctUsed: dp * 100,
    eventResults,
    windowResults,
    summary: windowResults.map(w => ({ window: w.window, pass: w.allPass })),
  };
}

// --- Exports ---
// Support both ESM and CJS
if (typeof module !== "undefined" && module.exports) {
  module.exports = { PARAM_BOUNDS, DEFAULT_CONFIG, DEFAULT_CONSTRAINT_CONFIG, HISTORICAL_ETH_EVENTS, HISTORICAL_WORST_BY_WINDOW, VALID_WINDOWS, analyze, sweep, multiSweep, findBoundaries, backtest, clampConfig, getEffectiveMax, getEffectiveMin, resolveWindow };
}

export { PARAM_BOUNDS, DEFAULT_CONFIG, DEFAULT_CONSTRAINT_CONFIG, HISTORICAL_ETH_EVENTS, HISTORICAL_WORST_BY_WINDOW, VALID_WINDOWS, analyze, sweep, multiSweep, findBoundaries, backtest, clampConfig, getEffectiveMax, getEffectiveMin, resolveWindow };
